import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { cn } from '@/lib/cn'
import { glassSurface } from '@/lib/glassSurface'
import {
  goldGradientButton,
  goldGradientIconChip,
  goldGradientText,
} from '@/lib/brandGradient'
import { useAppSelector } from '@/hooks/redux'
import {
  fetchMyWaitlistStatus,
  joinWaitlistAndNotify,
} from '@/services/waitlistService'

const PITCHOS_FEATURE_ID = 'pitchos'

const CloseIcon = () => (
  <svg aria-hidden className="h-5 w-5" viewBox="0 0 24 24" fill="none">
    <path
      d="M18 6L6 18M6 6l12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

const DeckIcon = ({ className }: { className?: string }) => (
  <svg
    aria-hidden
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="13" rx="2" />
    <path d="M7 8h6M7 11.5h10M7 15h4" />
    <path d="M9 20h6" />
  </svg>
)

const pitchOsSoonBadge =
  'rounded-full border border-[#E0A263]/40 bg-[#F6CB86]/10 px-2 py-0.5 text-[9px] font-semibold tracking-[0.12em] text-[#8a5e1a] uppercase dark:border-[#F6CB86]/25 dark:text-[#F6CB86]'

/** Pitch OS wordmark — header nav (no badge). */
export function PitchOSWordmark({
  className,
  size = 'md',
}: {
  className?: string
  size?: 'sm' | 'md'
}) {
  return (
    <span
      className={cn(
        'inline-flex items-baseline gap-1 font-bold tracking-tight',
        size === 'md' && 'text-[22px]',
        size === 'sm' && 'text-[12px] sm:text-[13px]',
        className,
      )}
    >
      <span className={goldGradientText}>Pitch</span>
      <span className={goldGradientText}>OS</span>
    </span>
  )
}

/** Pitch OS wordmark + Coming soon pill — coming-soon modal only. */
export function PitchOSLabel({
  className,
  size = 'md',
}: {
  className?: string
  size?: 'sm' | 'md'
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2',
        size === 'md' && 'text-[22px] font-bold tracking-tight',
        size === 'sm' && 'text-[12px] font-bold tracking-tight sm:text-[13px]',
        className,
      )}
    >
      <PitchOSWordmark size={size} />
      <span className={pitchOsSoonBadge}>Coming soon</span>
    </span>
  )
}

export function PitchDeckComingSoonModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const navigate = useNavigate()
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated)
  const [submitting, setSubmitting] = useState(false)
  const [notifyState, setNotifyState] = useState<
    'idle' | 'done' | 'already' | 'error'
  >('idle')

  useBodyScrollLock(open)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) setNotifyState('idle')
  }, [open])

  useEffect(() => {
    if (!open || !isAuthenticated) return
    let cancelled = false
    fetchMyWaitlistStatus(PITCHOS_FEATURE_ID)
      .then((s) => {
        if (cancelled) return
        if (s.joined) setNotifyState('already')
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [open, isAuthenticated])

  const handleNotify = async () => {
    if (!isAuthenticated) {
      onClose()
      navigate('/login')
      return
    }
    setSubmitting(true)
    try {
      const result = await joinWaitlistAndNotify(PITCHOS_FEATURE_ID)
      setNotifyState(result.alreadyOnList ? 'already' : 'done')
      setTimeout(() => onClose(), 1600)
    } catch {
      setNotifyState('error')
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  const modal = (
    <div
      className="fixed inset-0 z-[100] flex min-h-[100dvh] items-center justify-center bg-[#050505]/82 px-4 py-8 backdrop-blur-[14px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pitchos-coming-soon-title"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        className={cn(
          'relative z-[1] box-border flex w-[420px] max-w-[min(420px,calc(100vw-2rem))] flex-col items-center overflow-hidden rounded-3xl px-6 pt-7 pb-6 text-center shadow-2xl',
          'shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_24px_64px_-12px_rgba(0,0,0,0.75)]',
          glassSurface,
        )}
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-[#F6CB86]/10 to-transparent"
          aria-hidden
        />

        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 grid h-8 w-8 place-items-center rounded-full text-white/70 transition hover:bg-white/[0.08] hover:text-white"
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        <div
          className={cn(
            'relative mx-auto grid h-11 w-11 place-items-center rounded-xl',
            goldGradientIconChip,
          )}
        >
          <DeckIcon className="h-5 w-5" />
        </div>

        <div className="relative mt-3 flex justify-center">
          <PitchOSWordmark />
        </div>

        <span
          className={cn(
            'relative mt-2 inline-flex items-center',
            pitchOsSoonBadge,
          )}
        >
          Coming soon
        </span>

        <h2
          id="pitchos-coming-soon-title"
          className="relative mx-auto mt-3 max-w-[300px] text-[15px] leading-snug font-medium text-balance text-white"
        >
          Turn your validated idea into a fundable deck in minutes.
        </h2>

        <p className="relative mt-2 text-[12px] text-white/50">
          No designer · No consultant · No blank page
        </p>

        <p className="relative mx-auto mt-4 max-w-[320px] text-[13px] leading-relaxed text-balance text-white/65">
          A validated idea and a fundable idea are not the same thing.
        </p>
        <p className="relative mx-auto mt-2 max-w-[320px] text-[13px] leading-relaxed text-pretty text-white/65">
          The gap between them is usually a deck. Most decks lose room in the
          first three slides.
        </p>

        <div className="relative mt-4 flex w-full flex-col items-center">
          <button
            type="button"
            disabled={
              submitting || notifyState === 'done' || notifyState === 'already'
            }
            onClick={handleNotify}
            className={cn(
              'mt-3 inline-flex h-9 shrink-0 items-center justify-center px-5 text-[13px] font-semibold disabled:cursor-not-allowed disabled:opacity-80',
              goldGradientButton,
            )}
          >
            {submitting
              ? 'Adding you…'
              : notifyState === 'done'
                ? "You're on the list ✓"
                : notifyState === 'already'
                  ? 'Already on the list ✓'
                  : notifyState === 'error'
                    ? 'Try again'
                    : 'Notify Me'}
          </button>
        </div>
      </div>
    </div>
  )

  if (typeof document === 'undefined') return modal
  return createPortal(modal, document.body)
}
